import { action, computed, makeObservable, observable } from 'mobx';
import { arrayWithoutExistingItem } from '../../common/array';
import { EmailTemplate, EmailTemplateAttachment, EmailTemplateRequest } from '../../models';
import { emailAddressSchema } from '../../models/schemas/email-address.schema';
import { getEmailTemplateRequestSchema } from '../../models/schemas/email-template-request.schema';
import { format } from 'date-fns';
import { clone, isEqual, pick } from 'es-toolkit';
import {
  formatEmailAddresses,
  isValidEmail,
  parseEmailAddresses,
} from '../../shared/functions';
import {
  createValidationState,
  ValidationState,
} from '../../shared/validation';

const COMPARABLE_FIELDS = [
  'title',
  'bannerImageUrl',
  'bodyMessage',
  'sendOnDateTime',
  'sendOnTimeZone',
  'isSendNow',
  'targetGroupIds',
  'individualRecipients',
] as const;

const AVAILABLE_CHARS_LENGTH = ['title'] as const;
type CharLengthFields = (typeof AVAILABLE_CHARS_LENGTH)[number];

type OperationType = 'create' | 'update';

export class CreateOrEditEmailTemplatePageUiStore {
  @observable isBeingValidated = false;

  @observable isForSending = false;

  @observable selectedEmailTemplate?: EmailTemplate;

  @observable emailTemplateRequestBody?: EmailTemplateRequest;

  @observable individualRecipientsInput?: string;

  @observable selectedBannerImageFile?: File;

  @observable emailTemplateAttachments: EmailTemplateAttachment[] = [];

  @observable selectedAttachments: File[] = [];

  @observable isDateTimeToSendValid = false;

  @observable dateTimeValidationMessage?: string;

  @observable isAttachmentTotalSizeToSendValid = false;

  @observable attachmentTotalSizeValidationMessage?: string;

  @observable attachmentTicketId?: string;

  constructor() {
    makeObservable(this);
  }

  @action
  resetStates() {
    this.isBeingValidated = false;
    this.isForSending = false;
    this.selectedEmailTemplate = undefined;
    this.emailTemplateRequestBody = undefined;
    this.individualRecipientsInput = undefined;
    this.selectedBannerImageFile = undefined;
    this.selectedAttachments = [];
    this.isDateTimeToSendValid = false;
    this.dateTimeValidationMessage = undefined;
    this.isAttachmentTotalSizeToSendValid = false;
    this.attachmentTotalSizeValidationMessage = undefined;
    this.emailTemplateAttachments = [];
    this.attachmentTicketId = undefined;
  }

  @action
  setIsBeingValidated(isBeingValidated: boolean): void {
    this.isBeingValidated = isBeingValidated;
  }

  @action
  setIsForSending(isForSending: boolean): void {
    this.isForSending = isForSending;
  }

  @action
  createEmailTemplateRequestBody(): void {
    const initialValue: EmailTemplateRequest = {
      title: '',
      bodyMessage: '',
      sendOnDateTime: format(new Date(new Date().toString()), "yyyy-MM-dd'T'HH:mm"),
      sendOnTimeZone: 'Pacific Standard Time',
      isSendNow: false,
      targetGroupIds: [],
      individualRecipients: [],
    };

    this.emailTemplateRequestBody = initialValue;
  }

  @action
  createEmailTemplateRequestBodyForUpdate(existingEmailTemplate: EmailTemplate): void {
    const initialValue: EmailTemplateRequest = {
      title: existingEmailTemplate.title,
      bannerImageUrl: existingEmailTemplate.bannerImageUrl,
      bodyMessage: existingEmailTemplate.bodyMessage,
      sendOnDateTime: existingEmailTemplate.sendOnDateTime,
      sendOnTimeZone: existingEmailTemplate.sendOnTimeZone,
      isSendNow: existingEmailTemplate.isSendNow,
      targetGroupIds: existingEmailTemplate.targetGroupIds,
      individualRecipients: existingEmailTemplate.individualRecipients,
    };

    this.emailTemplateRequestBody = initialValue;
    this.emailTemplateAttachments = existingEmailTemplate.attachments;
    this.selectedEmailTemplate = existingEmailTemplate;
  }

  @action
  setAttachmentTicketId(ticketId?: string): void {
    this.attachmentTicketId = ticketId;
  }

  @action
  setBannerImageFile(file?: File): void {
    this.selectedBannerImageFile = file;
  }

  @action
  addAttachment(file: File): void {
    this.selectedAttachments.push(file);
  }

  @action
  removeAttachment(file: File): void {
    var attachmentToDelete = this.selectedAttachments.find((attachment) => attachment.name === file.name);
    if (attachmentToDelete) {
      this.selectedAttachments = arrayWithoutExistingItem(this.selectedAttachments, attachmentToDelete);
    }
  }

  @action
  removeExistingAttachment(emailTemplateAttachmentId: number): void {
    if (!this.emailTemplateRequestBody) return;
    var attachmentToDelete = this.emailTemplateAttachments.find((attachment) => attachment.emailTemplateAttachmentId === emailTemplateAttachmentId);
    if (attachmentToDelete) {
      this.emailTemplateAttachments = arrayWithoutExistingItem(this.emailTemplateAttachments, attachmentToDelete);
    }
  }

  @action
  setEmailTemplateRequestBody<T>(updatedField: { key: keyof EmailTemplate; value: T }): void {
    if (!this.emailTemplateRequestBody) return;

    const { key, value } = updatedField;
    const updatedEmailTemplateRequestBody = { ...this.emailTemplateRequestBody, [key]: value };
    this.emailTemplateRequestBody = updatedEmailTemplateRequestBody;
  }

  @action
  setIndividualRecipientsInput(individualRecipientsInput?: string): void {
    this.individualRecipientsInput = individualRecipientsInput;
  }

  @action
  addIndividualRecipients(emailAddresses: string[]): void {
    const individualRecipients = clone(this.individualRecipients);
    const invalidEmailAddresses: string[] = [];

    emailAddresses.forEach((emailAddress) => {
      if (isValidEmail(emailAddress)) {
        if (!individualRecipients.includes(emailAddress)) individualRecipients.push(emailAddress);
      } else {
        invalidEmailAddresses.push(emailAddress);
      }
    });

    if (individualRecipients.length > this.individualRecipients.length) {
      this.setEmailTemplateRequestBody<string[]>({
        key: 'individualRecipients',
        value: individualRecipients,
      });
    }

    this.individualRecipientsInput = invalidEmailAddresses.length === 0
      ? undefined
      : formatEmailAddresses(invalidEmailAddresses);
  }

  @action
  addTargetGroup(targetGroupId: number): void {
    if (!this.emailTemplateRequestBody) return;

    const currentTargetGroupIds = this.emailTemplateRequestBody.targetGroupIds || [];
    if (!currentTargetGroupIds.includes(targetGroupId)) {
      this.emailTemplateRequestBody = {
        ...this.emailTemplateRequestBody,
        targetGroupIds: [...currentTargetGroupIds, targetGroupId],
      };
    }
  }

  @action
  removeTargetGroup(targetGroupId: number): void {
    if (!this.emailTemplateRequestBody) return;

    const currentTargetGroupIds = this.emailTemplateRequestBody.targetGroupIds || [];
    this.emailTemplateRequestBody = {
      ...this.emailTemplateRequestBody,
      targetGroupIds: currentTargetGroupIds.filter((id) => id !== targetGroupId),
    };
  }

  @action
  setSendNow(): void {
    if (!this.emailTemplateRequestBody) return;

    this.emailTemplateRequestBody = {
      ...this.emailTemplateRequestBody,
      isSendNow: true,
      sendOnDateTime: undefined,
      sendOnTimeZone: undefined,
    };
  }

  @action
  setScheduledSend(): void {
    if (!this.emailTemplateRequestBody) return;

    this.emailTemplateRequestBody = {
      ...this.emailTemplateRequestBody,
      isSendNow: false,
      sendOnDateTime: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
      sendOnTimeZone: 'Pacific Standard Time',
    };
  }

  @action
  setAttachments(emailTemplateAttachments: EmailTemplateAttachment[]): void {
    if (this.operationType === 'update') {
      this.emailTemplateAttachments = [...this.emailTemplateAttachments, ...emailTemplateAttachments];
    } else {
      this.emailTemplateAttachments = [...emailTemplateAttachments];
    }
  }

  @action
  setDateTimeToSendValid(isValid: boolean): void {
    this.isDateTimeToSendValid = isValid;
  }

  @action
  setDateTimeValidationMessage(validationMessage?: string): void {
    this.dateTimeValidationMessage = validationMessage;
  }

  @action
  setIsAttachmentTotalSizeToSendValid(isValid: boolean): void {
    this.isAttachmentTotalSizeToSendValid = isValid;
  }

  @action
  setAttachmentTotalSizeValidationMessage(validationMessage?: string): void {
    this.attachmentTotalSizeValidationMessage = validationMessage;
  }

  @computed
  get operationType(): OperationType | undefined {
    if (this.selectedEmailTemplate) return 'update';
    if (this.emailTemplateRequestBody) return 'create';
    return undefined;
  }

  @computed
  get emailTemplateRequestValidationForSaving(): ValidationState<EmailTemplateRequest> {
    return createValidationState(
      getEmailTemplateRequestSchema(false).safeParse(this.emailTemplateRequestBody),
    );
  }

  @computed
  get emailTemplateRequestValidationForSending(): ValidationState<EmailTemplateRequest> {
    return createValidationState(
      getEmailTemplateRequestSchema(true).safeParse(this.emailTemplateRequestBody),
    );
  }

  @computed
  get emailTemplateRequestValidation(): ValidationState<EmailTemplateRequest> {
    return this.isForSending
      ? this.emailTemplateRequestValidationForSending
      : this.emailTemplateRequestValidationForSaving;
  }

  @computed
  get individualRecipients(): string[] {
    return this.emailTemplateRequestBody?.individualRecipients ?? [];
  }

  @computed
  get hasIndividualRecipients(): boolean {
    return this.individualRecipients.length > 0;
  }

  getIndividualRecipientValidationMessages(isBeingValidated: boolean): string[] {
    const validationMessages: string[] = [];

    if (isBeingValidated) {
      parseEmailAddresses(this.individualRecipientsInput || '').forEach((emailAddress) => {
        const result = emailAddressSchema.safeParse(emailAddress);
        if (result.success) return;

        result.error.issues.forEach((issue) => {
          if (!validationMessages.includes(issue.message)) validationMessages.push(issue.message);
        });
      });
    }

    if (validationMessages.length > 0) return validationMessages;

    return this.isBeingValidated
      ? this.emailTemplateRequestValidation.fieldErrors.individualRecipients ?? []
      : [];
  }

  @computed
  get charsLength(): Record<CharLengthFields, number> {
    const defaultLengths: Record<CharLengthFields, number> = {
      title: 0,
    };

    if (!this.emailTemplateRequestBody) {
      return defaultLengths;
    }

    const requestBody = this.emailTemplateRequestBody;

    return AVAILABLE_CHARS_LENGTH.reduce(
      (acc, fieldName) => {
        acc[fieldName] = requestBody[fieldName]?.length || 0;
        return acc;
      },
      { ...defaultLengths },
    );
  }

  @computed
  get hasChanges(): boolean {
    if (!this.emailTemplateRequestBody) return false;
    if (!this.selectedEmailTemplate) return true;

    const originalFields = pick(this.selectedEmailTemplate, COMPARABLE_FIELDS);
    const currentFields = pick(this.emailTemplateRequestBody, COMPARABLE_FIELDS);

    const hasFieldsChanged = !isEqual(originalFields, currentFields);

    return hasFieldsChanged;
  }

  @computed
  get isNoEmailTemplateChanges(): boolean {
    if (!this.selectedEmailTemplate) return true;
    const isNotUploadingNewAttachments = this.selectedAttachments.length === 0;
    const isNotUploadingNewBannerImage = this.selectedBannerImageFile === undefined;
    const isNoChangesHasBeenMade = this.hasChanges;
    return isNotUploadingNewAttachments && isNotUploadingNewBannerImage && isNoChangesHasBeenMade;
  }
}
